import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useParams, useSearchParams } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import OrderDetails from "./OrderDetails";
import VerifyNumber from "./VerifyNumber";
import OTPVerify from "./OTPVerify";
import StripePayment from "./StripePayment";
import Success from "./Success";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import { setOrderData, setStoreData, setCurrencySymbol } from "../redux/action";
import { getOrder, getSingleStore, getTableOrderDetail, updateOrderStatus } from "./payAtTableApi";
import { normaliseOrder } from "./constants";
import "./payAtTable.css";

const STEPS = {
  ORDER: "order",
  VERIFY_NUMBER: "verify_number",
  OTP: "otp",
  STRIPE: "stripe",
  SUCCESS: "success",
};

const PAYMENT_PENDING_ORDER_KEY = "payAtTable_pending_order_id";
const PAYMENT_PENDING_ENDPOINT_KEY = "payAtTable_pending_endpoint";

function maskPhoneForDisplay(phone) {
  if (!phone || phone.length < 8) return null;
  const cleaned = phone.replace(/\D/g, "");
  const len = cleaned.length;
  if (len <= 4) return "+" + cleaned.slice(-4).padStart(4, "*");
  const lastTwo = cleaned.slice(-2);
  const firstPart = cleaned.slice(0, Math.min(2, len - 4));
  const mid = "*".repeat(Math.max(0, len - 4 - firstPart.length));
  return `+${firstPart}${mid} ${lastTwo}`;
}

export default function PayAtTable() {
  useDocumentTitle("Pay at Table");
  const { tableToken, storeID, tableNumber } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();
  const { endpoint, store, orderData } = useSelector((state) => state.auth);
  const [step, setStep] = useState(STEPS.ORDER);
  const [phone, setPhone] = useState("");
  const [orderDetails, setOrderDetails] = useState(null);
  const [storeInfo, setStoreInfo] = useState(null);
  const [orderLoading, setOrderLoading] = useState(true);
  const [orderError, setOrderError] = useState(null);
  const [successOrderRef, setSuccessOrderRef] = useState(null);
  const [successMaskedPhone, setSuccessMaskedPhone] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [storeLoading, setStoreLoading] = useState(true);

  // Order ID: route param (tableToken as orderId) first, then Redux
  const orderIdToFetch = useMemo(
    () => tableToken || orderData?.order?.id || orderData?.id,
    [tableToken, orderData]
  );

  // Store details: local store fetch → order.store[0] → Redux orderData.order.store[0] → Redux store
  const storeDetails = useMemo(
    () =>
      storeInfo ||
      orderDetails?.store?.[0] ||
      orderData?.order?.store?.[0] ||
      (store?.name ? store : null) ||
      {},
    [storeInfo, orderDetails, orderData, store]
  );

  // Store ID for Stripe config: URL param first, then from order/store
  const storeIdForPayment = useMemo(
    () =>
      storeID ||
      storeDetails?.id ||
      orderDetails?.store_id ||
      orderDetails?.store?.[0]?.id,
    [storeID, storeDetails, orderDetails]
  );

  // Normalised order for UI; use URL tableNumber when provided
  const normalisedOrder = useMemo(() => {
    const normalised = orderDetails ? normaliseOrder(orderDetails) : null;
    if (normalised && tableNumber != null && tableNumber !== "") {
      return { ...normalised, tableId: String(tableNumber) };
    }
    return normalised;
  }, [orderDetails, tableNumber]);

  // 1) Fetch store by storeId only. On success: set store + currency, then fetch table order (storeId + tableNumber) → order ID → full order.
  useEffect(() => {
    if (!storeID || !endpoint) return;
    let cancelled = false;
    setStoreLoading(true);
    setOrderError(null);

    getSingleStore(endpoint, storeID)
      .then((store) => {
        if (cancelled) return;
        if (!store) {
          setOrderError("Store not found");
          return;
        }
        setStoreInfo(store);
        dispatch(setStoreData(store));
        if (store.currency_code != null) dispatch(setCurrencySymbol(store.currency_code));

        const hasTableNumber = tableNumber != null && tableNumber !== "";
        if (!hasTableNumber) return;

        setOrderLoading(true);
        return getTableOrderDetail(endpoint, storeID, tableNumber)
          .then((orderId) => (cancelled ? null : getOrder(endpoint, orderId)))
          .then((order) => {
            if (cancelled) return;
            setOrderDetails(order || null);
            if (order) dispatch(setOrderData({ order }));
            setOrderError(null);
          })
          .catch((err) => {
            if (!cancelled) setOrderError(err?.message || "Failed to load order");
          })
          .finally(() => {
            if (!cancelled) setOrderLoading(false);
          });
      })
      .catch(() => {
        if (!cancelled) setOrderError("Store not found");
      })
      .finally(() => {
        if (!cancelled) setStoreLoading(false);
      });

    return () => { cancelled = true; };
  }, [endpoint, storeID, tableNumber, dispatch]);

  // 2) Fetch order by orderId only when NOT using storeId+tableNumber (e.g. /pay-at-table/:tableToken)
  useEffect(() => {
    const useStoreAndTable = storeID && tableNumber != null && tableNumber !== "";
    if (!orderIdToFetch || !endpoint || useStoreAndTable) return;

    let cancelled = false;
    setOrderLoading(true);
    setOrderError(null);
    getOrder(endpoint, orderIdToFetch)
      .then((order) => {
        if (cancelled) return;
        setOrderDetails(order || null);
        if (order) dispatch(setOrderData({ order }));
      })
      .catch((err) => {
        if (!cancelled) setOrderError(err?.message || "Failed to load order");
      })
      .finally(() => {
        if (!cancelled) setOrderLoading(false);
      });
    return () => { cancelled = true; };
  }, [endpoint, orderIdToFetch, storeID, tableNumber, dispatch]);

  // Persist order ref and phone when on payment step (for success screen after 3DS redirect)
  useEffect(() => {
    if (step !== STEPS.STRIPE) return;
    if (normalisedOrder?.orderReference) {
      sessionStorage.setItem("payAtTable_success_order_ref", normalisedOrder.orderReference);
    }
    const masked = maskPhoneForDisplay(phone);
    if (masked) sessionStorage.setItem("payAtTable_success_masked_phone", masked);
  }, [step, normalisedOrder?.orderReference, phone]);

  // Handle return from Stripe 3DS redirect
  useEffect(() => {
    const redirectStatus = searchParams.get("redirect_status");
    const paymentIntentClientSecret = searchParams.get("payment_intent_client_secret");
    if (redirectStatus !== "succeeded" || !paymentIntentClientSecret) return;
    const pendingOrderId = sessionStorage.getItem(PAYMENT_PENDING_ORDER_KEY);
    const pendingEndpoint = sessionStorage.getItem(PAYMENT_PENDING_ENDPOINT_KEY);
    if (!pendingOrderId || !pendingEndpoint) return;
    let cancelled = false;
    updateOrderStatus(pendingEndpoint, pendingOrderId, {
      payment_type: "card",
      order_status: 1,
      payment_status: 1,
    })
      .then(() => {
        if (cancelled) return;
      sessionStorage.removeItem(PAYMENT_PENDING_ORDER_KEY);
      sessionStorage.removeItem(PAYMENT_PENDING_ENDPOINT_KEY);
      setSearchParams({}, { replace: true });
      const savedRef = sessionStorage.getItem("payAtTable_success_order_ref");
      const savedPhone = sessionStorage.getItem("payAtTable_success_masked_phone");
      if (savedRef) setSuccessOrderRef(savedRef);
      if (savedPhone) setSuccessMaskedPhone(savedPhone);
      setStep(STEPS.SUCCESS);
      })
      .catch(() => {
        if (!cancelled) {
          sessionStorage.removeItem(PAYMENT_PENDING_ORDER_KEY);
          sessionStorage.removeItem(PAYMENT_PENDING_ENDPOINT_KEY);
        }
      });
    return () => { cancelled = true; };
  }, [searchParams, setSearchParams]);

  const currentStepIndex = useMemo(() => {
    switch (step) {
      case STEPS.ORDER:
        return 0;
      case STEPS.VERIFY_NUMBER:
        return 1;
      case STEPS.OTP:
        return 2;
      case STEPS.STRIPE:
      case STEPS.SUCCESS:
        return 3;
      default:
        return 0;
    }
  }, [step]);

  const stepLabel = useMemo(() => {
    switch (step) {
      case STEPS.ORDER:
        return "Review your order";
      case STEPS.VERIFY_NUMBER:
        return "Verify your number";
      case STEPS.OTP:
        return "Enter verification code";
      case STEPS.STRIPE:
        return "Complete payment";
      case STEPS.SUCCESS:
        return "Payment complete";
      default:
        return "";
    }
  }, [step]);

  const handleContinueToPay = useCallback(() => {
    setStep(STEPS.VERIFY_NUMBER);
  }, []);

  const handleSendCode = useCallback(async (phoneNumber) => {
    setPhone(phoneNumber);
    setStep(STEPS.OTP);
  }, []);

  const handleVerifyOtp = useCallback(async () => {
    setStep(STEPS.STRIPE);
  }, []);

  const handleResendOtp = useCallback(async () => {
    // Prototype: no-op; in real app would call resend API
  }, []);

  const handlePay = useCallback(async () => {
    setStep(STEPS.SUCCESS);
  }, []);

  const renderStepContent = () => {
    if (step === STEPS.ORDER) {
      return (
        <OrderDetails
          storeDetails={storeDetails}
          order={normalisedOrder}
          orderLoading={orderLoading}
          orderError={orderError}
          onContinue={handleContinueToPay}
        />
      );
    }
    if (step === STEPS.VERIFY_NUMBER) {
      return <VerifyNumber onSendCode={handleSendCode} />;
    }
    if (step === STEPS.OTP) {
      return (
        <OTPVerify
          phone={phone}
          onVerify={handleVerifyOtp}
          onResend={handleResendOtp}
        />
      );
    }
    if (step === STEPS.STRIPE) {
      const orderId = normalisedOrder?.orderId ?? orderDetails?.id;
      return (
        <StripePayment
          order={normalisedOrder}
          orderId={orderId}
          endpoint={endpoint}
          storeId={storeIdForPayment}
          onPay={handlePay}
        />
      );
    }
    if (step === STEPS.SUCCESS) {
      return (
        <Success
          maskedPhone={successMaskedPhone ?? maskPhoneForDisplay(phone)}
          orderReference={successOrderRef ?? normalisedOrder?.orderReference}
        />
      );
    }
    return null;
  };

  const totalSteps = 4;

  return (
    <div className="pay-at-table-shell">
      <div className="pay-at-table-stage">
        <div className="pay-at-table-stepper">
          <div>
            <div className="pay-at-table-step-label">Pay at table</div>
            <div className="pay-at-table-step-pill">
              Step {Math.min(currentStepIndex + 1, totalSteps)} of {totalSteps} ·{" "}
              {stepLabel}
            </div>
          </div>
          <div className="pay-at-table-step-dot-row">
            {Array.from({ length: totalSteps }).map((_, index) => (
              <span
                key={index}
                className={
                  "pay-at-table-step-dot" +
                  (index === currentStepIndex ? " is-active" : "")
                }
              />
            ))}
          </div>
        </div>
        {renderStepContent()}
      </div>
    </div>
  );
}
